import { useEffect, useState } from "react";
import axios from "axios";
import { logWarn } from "./log";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Session lives in HttpOnly cookies; the access token is kept only in memory for this tab.
let memToken = null;
localStorage.removeItem("aurora_token");

export const getToken = () => memToken;
export const setToken = (t) => { memToken = t || null; };

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((c) => {
  const t = getToken();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export async function tryRefresh() {
  const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
  setToken(data.token);
  return data.token;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    if (err.response?.status === 401 && !cfg._retry && !String(cfg.url).includes("/auth/")) {
      cfg._retry = true;
      try {
        await tryRefresh();
        return api(cfg);
      } catch (refreshErr) {
        logWarn("Rinnovo sessione non riuscito", refreshErr);
      }
    }
    return Promise.reject(err);
  }
);

export function errMsg(e, fallback = "Si è verificato un errore. Riprova.") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message && !e.response ? fallback : fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || fallback;
  return d.msg || fallback;
}

export async function streamChat(body, onEvent, signal) {
  const doFetch = () =>
    fetch(`${API}/chat/stream`, {
      method: "POST",
      signal,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
      body: JSON.stringify(body),
    });
  let res = await doFetch();
  if (res.status === 401) {
    await tryRefresh();
    res = await doFetch();
  }
  if (!res.ok) {
    let detail = "Errore di comunicazione con Aurora";
    try { detail = (await res.json()).detail || detail; } catch (parseErr) { logWarn("Risposta errore non JSON", parseErr); }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (line.startsWith("data:")) {
        try { onEvent(JSON.parse(line.slice(5).trim())); } catch (parseErr) { logWarn("Evento SSE non valido", parseErr); }
      }
    }
  }
}

// Loads a private image with the auth header and exposes it as an object URL.
export function useFileUrl(iid) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj = null;
    let alive = true;
    api.get(`/images/${iid}/file`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); if (alive) setUrl(obj); })
      .catch((e) => logWarn("Immagine non caricata", e));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [iid]);
  return url;
}
